// BaseFlow Client - Main client class

import { BaseFlowClientOptions, QueryBuilder, AuthEvent } from './types';
import { PostgrestQueryBuilder } from './query-builder';

/**
 * The main client for interacting with a BaseFlow backend.
 *
 * @example
 * ```ts
 * const client = createClient({
 *  url: 'http://localhost:4000',
 *  apiKey: 'your-api-key'
 * });
 *
 * const { data, error } = await client.from('users').select('*');
 * ```
 */
export class BaseFlowClient {
  /** The URL of the BaseFlow backend. */
  private url: string;
  /** The API key for the BaseFlow backend. */
  private apiKey: string;
  /** Additional headers to send with each request. */
  private headers: Record<string, string>;
  /** The underlying fetch implementation. */
  private fetch: typeof fetch;
  /** The authentication API. */
  public auth: AuthAPI;
  /** The storage API. */
  public storage: StorageAPI;
  /** The realtime API. */
  public realtime: RealtimeAPI;

  /**
   * Creates a new BaseFlowClient instance.
   *
   * @param options The options for the client.
   */
  constructor(options: BaseFlowClientOptions) {
    this.url = options.url.replace(/\/$/, ''); // Remove trailing slash
    this.apiKey = options.apiKey;
    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${options.apiKey}`,
      'x-api-key': options.apiKey,
      ...options.headers
    };
    
    // Use provided fetch or global fetch or cross-fetch
    // Use provided fetch or globalThis.fetch or cross-fetch
if (options.fetch) {
  this.fetch = options.fetch;
} else if (typeof globalThis.fetch === 'function') {
  this.fetch = (...args) => globalThis.fetch(...args);
} else {
  this.fetch = require('cross-fetch');
}

    
    // Initialize API modules
    this.storage = new StorageAPI(this);
    this.auth = new AuthAPI(this);
    this.realtime = new RealtimeAPI(this);
  }

  /**
   * Creates a query builder for a specific table.
   *
   * @param table The name of the table to query.
   * @returns A query builder for the specified table.
   */
  from<T = any>(table: string): QueryBuilder<T> {
    return new PostgrestQueryBuilder<T>(this, table);
  }

  /**
   * Makes a request to the BaseFlow backend.
   *
   * @param method The HTTP method to use.
   * @param path The path to request.
   * @param options Additional options for the request.
   * @returns The response from the backend.
   */
  async request(
    method: string,
    path: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
      params?: Record<string, any>;
      baseUrl?: string;
    } = {}
  ) {
    // Use custom base URL if provided, otherwise use the client's URL
    const baseUrl = options.baseUrl || this.url;
    const url = new URL(`${baseUrl}${path}`);
    
    // Add query parameters
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const requestHeaders = {
      ...this.headers,
      ...options.headers
    };

    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders
    };

    if (options.body && method !== 'GET' && method !== 'HEAD') {
      requestOptions.body = typeof options.body === 'string' 
        ? options.body 
        : JSON.stringify(options.body);
    }

    try {
      const response = await this.fetch(url.toString(), requestOptions);
      
      let data = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        return {
          data: null,
          error: {
            message: data?.error || data?.message || `HTTP ${response.status}`,
            code: data?.code || 'HTTP_ERROR',
            details: data
          },
          status: response.status,
          statusText: response.statusText
        };
      }

      return {
        data,
        error: null,
        status: response.status,
        statusText: response.statusText
      };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Network error',
          code: 'NETWORK_ERROR',
          details: error
        },
        status: 0,
        statusText: 'Network Error'
      };
    }
  }

  /**
   * Get project information
   */
  async getProjectInfo() {
    return this.request('GET', '/rest/v1/');
  }

  /**
   * Set authentication token
   */
  setAuth(token: string | null) {
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Execute a raw SQL query (if supported)
   */
  async sql(query: string, params: any[] = []) {
    return this.request('POST', '/rest/v1/sql', {
      body: { query, params }
    });
  }

  /**
   * Call a remote procedure/function
   */
  async rpc(functionName: string, params: any = {}) {
    return this.request('POST', `/rest/v1/rpc/${functionName}`, {
      body: params
    });
  }

  /**
   * Create a new function
   */
  async createFunction(name: string, definition: string, options: {
    language?: 'javascript' | 'sql';
    returnType?: string;
    parameters?: Array<{ name: string; type: string; default?: any }>;
    description?: string;
  } = {}) {
    return this.request('POST', '/rest/v1/functions', {
      body: { name, definition, ...options }
    });
  }

  /**
   * List all functions
   */
  async listFunctions() {
    return this.request('GET', '/rest/v1/functions');
  }

  /**
   * Get function details
   */
  async getFunction(name: string) {
    return this.request('GET', `/rest/v1/functions/${name}`);
  }

  /**
   * Delete a function
   */
  async deleteFunction(name: string) {
    return this.request('DELETE', `/rest/v1/functions/${name}`);
  }

  /**
   * Create an index
   */
  async createIndex(table: string, columns: string[], options: {
    name?: string;
    unique?: boolean;
  } = {}) {
    return this.request('POST', '/rest/v1/indexes', {
      body: { table, columns, ...options }
    });
  }

  /**
   * List indexes
   */
  async listIndexes(table?: string) {
    return this.request('GET', '/rest/v1/indexes', {
      params: table ? { table } : {}
    });
  }

  /**
   * Drop an index
   */
  async dropIndex(name: string) {
    return this.request('DELETE', `/rest/v1/indexes/${name}`);
  }

  /**
   * Analyze query performance
   */
  async analyzeQuery(query: string) {
    return this.request('POST', '/rest/v1/analyze', {
      body: { query }
    });
  }

  /**
   * Define and create tables from schema (BaseFlow-style)
   */
  async defineSchema(schema: Record<string, any>) {
    return this.request('POST', '/rest/v1/schema', {
      body: { tables: schema }
    });
  }

  /**
   * Get current project schema
   */
  async getSchema() {
    return this.request('GET', '/rest/v1/schema');
  }

  /**
   * Create a single table from definition
   */
  async createTable(tableName: string, definition: any) {
    return this.request('POST', '/rest/v1/rpc/create_table', {
      body: { table_name: tableName, schema: definition }
    });
  }
}

/**
 * Storage API for file operations
 */
class StorageAPI {
  constructor(private client: BaseFlowClient) {}

  async upload(path: string, file: Buffer | Uint8Array | string, options?: { mimetype?: string }) {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Convert different file types to Blob
      let blob: Blob;
      if (file instanceof Buffer) {
        blob = new Blob([new Uint8Array(file)], { type: options?.mimetype || 'application/octet-stream' });
      } else if (file instanceof Uint8Array) {
        blob = new Blob([new Uint8Array(file)], { type: options?.mimetype || 'application/octet-stream' });
      } else if (typeof file === 'string') {
        blob = new Blob([file], { type: options?.mimetype || 'text/plain' });
      } else {
        blob = file as Blob;
      }
      
      formData.append('file', blob, path.split('/').pop() || 'file');
      formData.append('path', path);
      
      if (options?.mimetype) {
        formData.append('mimetype', options.mimetype);
      }

      // Make request with FormData
      const url = new URL(`${this.client['url']}/storage/upload`);
      
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
          'Authorization': this.client['headers']['Authorization'],
          'x-api-key': this.client['headers']['x-api-key']
        },
        body: formData
      };

      const response = await this.client['fetch'](url.toString(), requestOptions);
      
      let data = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        return {
          data: null,
          error: {
            message: data?.error || data?.message || `HTTP ${response.status}`,
            code: data?.code || 'UPLOAD_ERROR',
            details: data
          }
        };
      }

      return {
        data: data,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Upload failed',
          code: 'NETWORK_ERROR',
          details: error
        }
      };
    }
  }

  async getUrl(path: string) {
    return this.client.request('GET', '/storage/url', {
      params: { path }
    });
  }

  async download(path: string) {
    return this.client.request('GET', '/storage/download', {
      params: { path }
    });
  }

  async delete(path: string) {
    return this.client.request('DELETE', '/storage/files', {
      params: { path }
    });
  }

  async list(path: string = '/') {
    return this.client.request('GET', '/storage/files', {
      params: { path }
    });
  }

  async createFolder(path: string) {
    return this.client.request('POST', '/storage/folders', {
      body: { path }
    });
  }
}

/**
 * Auth API for authentication operations
 */
class AuthAPI {
  private authToken: string | null = null;
  private user: any = null;
  private listeners: Array<(event: AuthEvent) => void> = [];

  constructor(private client: BaseFlowClient) {}

  /**
   * Sign up a new user
   */
  async signUp(email: string, password: string, options?: { 
    name?: string; 
    data?: Record<string, any>;
    redirectTo?: string;
  }) {
    const response = await this.client.request('POST', '/rest/v1/auth/signup', {
      body: { 
        email, 
        password, 
        name: options?.name,
        data: options?.data,
        redirect_to: options?.redirectTo
      }
    });

    if (response.data && response.data.user) {
      this.user = response.data.user;
      if (response.data.session && response.data.session.access_token) {
        this.authToken = response.data.session.access_token;
        this.client.setAuth(this.authToken);
        this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
      }
    }

    return response;
  }

  /**
   * Sign in with email and password
   */
  async signInWithPassword(credentials: { email: string; password: string }) {
    const response = await this.client.request('POST', '/rest/v1/auth/signin', {
      body: { 
        email: credentials.email, 
        password: credentials.password 
      }
    });

    if (response.data && response.data.user) {
      this.user = response.data.user;
      if (response.data.session && response.data.session.access_token) {
        this.authToken = response.data.session.access_token;
        this.client.setAuth(this.authToken);
        this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
      }
    }

    return response;
  }

  /**
   * Sign in with OAuth provider (GitHub, Google, etc.)
   */
  async signInWithOAuth(provider: 'github' | 'google', options?: {
    redirectTo?: string;
    scopes?: string;
  }) {
    const response = await this.client.request('POST', '/rest/v1/auth/oauth/signin', {
      body: { 
        provider,
        redirect_to: options?.redirectTo,
        scopes: options?.scopes
      }
    });

    if (response.data && response.data.url) {
      // Return the OAuth URL for the client to redirect to
      return {
        data: { url: response.data.url },
        error: null
      };
    }

    return response;
  }

  /**
   * Handle OAuth callback and exchange code for session
   */
  async handleOAuthCallback(code: string, provider: 'github' | 'google') {
    const response = await this.client.request('POST', '/rest/v1/auth/oauth/callback', {
      body: { code, provider }
    });

    if (response.data && response.data.user) {
      this.user = response.data.user;
      if (response.data.session && response.data.session.access_token) {
        this.authToken = response.data.session.access_token;
        this.client.setAuth(this.authToken);
        this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
      }
    }

    return response;
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    const response = await this.client.request('POST', '/rest/v1/auth/signout');

    this.authToken = null;
    this.user = null;
    this.client.setAuth(null);
    this.notifyListeners({ type: 'SIGNED_OUT' });

    return response;
  }

  /**
   * Get the current user
   */
  async getUser() {
    if (!this.authToken) {
      return { user: null, error: null };
    }

    const response = await this.client.request('GET', '/rest/v1/auth/user');

    if (response.data && response.data.user) {
      this.user = response.data.user;
    }

    return response;
  }

  /**
   * Get the current session
   */
  getSession() {
    return {
      access_token: this.authToken,
      user: this.user,
      expires_at: null, // TODO: Add token expiry
      refresh_token: null // TODO: Add refresh token support
    };
  }

  // Note: updateUser, resetPasswordForEmail, and verifyOtp are not yet implemented
  // in the current BaseFlow version. These methods are placeholders for future features.

  /**
   * Set the auth token manually
   */
  setSession(session: { access_token: string; user?: any }) {
    this.authToken = session.access_token;
    this.user = session.user || null;
    this.client.setAuth(this.authToken);
    
    if (this.user) {
      this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (event: AuthEvent) => void) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of auth events
   */
  private notifyListeners(event: AuthEvent) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Auth listener error:', error);
      }
    });
  }

  // Legacy methods for backward compatibility
  async signup(email: string, password: string, name?: string) {
    return this.signUp(email, password, { name });
  }

  async login(email: string, password: string) {
    return this.signInWithPassword({ email, password });
  }

  async logout() {
    return this.signOut();
  }
}

/**
 * Realtime API for WebSocket subscriptions
 */
class RealtimeAPI {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, Set<(payload: any) => void>> = new Map();
  private authenticated: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;

  constructor(private client: BaseFlowClient) {}

  /**
   * Connect to realtime WebSocket
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Extract base URL and create WebSocket URL
        const baseUrl = this.client['url'];
        const wsUrl = baseUrl.replace(/^http/, 'ws') + '/realtime';
        
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('🔌 Connected to BaseFlow Realtime');
          this.reconnectAttempts = 0;
          this.authenticate().then(() => {
            resolve();
          }).catch(reject);
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Realtime message parse error:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('🔌 Disconnected from BaseFlow Realtime');
          this.authenticated = false;
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('Realtime WebSocket error:', error);
          reject(error);
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Authenticate with the realtime server
   */
  private async authenticate(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      // Get project info first
      this.client.getProjectInfo().then((projectInfo) => {
        if (projectInfo.error || !projectInfo.data?.project?.id) {
          reject(new Error('Failed to get project info'));
          return;
        }

        const authMessage = {
          type: 'auth',
          payload: {
            api_key: this.client['apiKey'],
            project_id: projectInfo.data.project.id
          }
        };

        // Set up one-time listener for auth response
        const authHandler = (message: any) => {
          if (message.type === 'auth') {
            if (message.event === 'authenticated') {
              this.authenticated = true;
              console.log('🔐 Authenticated with BaseFlow Realtime');
              resolve();
            } else if (message.event === 'error') {
              reject(new Error(message.payload.message || 'Authentication failed'));
            }
          }
        };

        // Temporarily add auth handler
        this.ws!.addEventListener('message', (event) => {
          const message = JSON.parse(event.data);
          authHandler(message);
        });

        this.ws!.send(JSON.stringify(authMessage));
      }).catch(reject);
    });
  }

  /**
   * Subscribe to table changes
   */
  subscribe(table: string, callback: (payload: any) => void): () => void {
    if (!this.subscriptions.has(table)) {
      this.subscriptions.set(table, new Set());
      
      // Send subscription message if connected
      if (this.ws && this.ws.readyState === WebSocket.OPEN && this.authenticated) {
        this.ws.send(JSON.stringify({
          type: 'subscribe',
          payload: { table, event: '*' }
        }));
      }
    }

    this.subscriptions.get(table)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscriptions.get(table);
      if (callbacks) {
        callbacks.delete(callback);
        
        // If no more callbacks, unsubscribe from table
        if (callbacks.size === 0) {
          this.subscriptions.delete(table);
          
          if (this.ws && this.ws.readyState === WebSocket.OPEN && this.authenticated) {
            this.ws.send(JSON.stringify({
              type: 'unsubscribe',
              payload: { table }
            }));
          }
        }
      }
    };
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: any): void {
    switch (message.type) {
      case 'broadcast':
        if (message.event === 'change') {
          const { table } = message.payload;
          const callbacks = this.subscriptions.get(table);
          if (callbacks) {
            callbacks.forEach(callback => {
              try {
                callback(message.payload);
              } catch (error) {
                console.error('Realtime callback error:', error);
              }
            });
          }
        }
        break;

      case 'subscription':
        if (message.event === 'subscribed') {
          console.log(`📡 Subscribed to ${message.payload.table}`);
        } else if (message.event === 'error') {
          console.error('Subscription error:', message.payload.message);
        }
        break;

      case 'error':
        console.error('Realtime error:', message.payload.message);
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.log('Unknown realtime message:', message);
    }
  }

  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error);
      });
    }, delay);
  }

  /**
   * Disconnect from realtime
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.authenticated = false;
    this.subscriptions.clear();
    this.reconnectAttempts = 0;
  }

  /**
   * Send ping to keep connection alive
   */
  ping(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }

  /**
   * Get connection status
   */
  getStatus(): { connected: boolean; authenticated: boolean; subscriptions: number } {
    return {
      connected: this.ws?.readyState === WebSocket.OPEN || false,
      authenticated: this.authenticated,
      subscriptions: this.subscriptions.size
    };
  }
}

/**
 * Create a new BaseFlow client instance
 */
export function createClient(options: BaseFlowClientOptions): BaseFlowClient {
  return new BaseFlowClient(options);
}